import { useEffect, useState, useContext } from "react";
import Navbar from "../Navbar";
import UserContext from "../../contexts/UserContext";
import {
  CollectionReference,
  DocumentData,
  collection,
  getDocs,
  Query,
  query,
  where,
  QuerySnapshot,
  getDoc,
  doc,
} from "firebase/firestore";
import { AccountDataObject } from "../../types/AccountDataObject";
import { RoundsDataObject } from "../../types/RoundsDataObject";
import { SettingsDataObject } from "../../types/SettingsDataObject";
import { useNavigate } from "react-router-dom";
import GDPR from "./GDPR";

/**
 * Renders the "/iron-sights" page
 * @returns html containing ...
 */
const UserData = () => {
  useEffect(() => {
    document.title = "User Data - FPS Aim Trainer";
  }, []);

  const userContext = useContext(UserContext);
  const navigate = useNavigate(); // react router hook to navgiate pages

  useEffect(() => {
    if (!userContext.user) {
      navigate("/login");
    }
  }, [userContext.user]);

  //https://stackoverflow.com/questions/66735331/how-display-data-from-firestore-in-a-react-web-app
  const [userAccountData, setUserAccountData] = useState<AccountDataObject[]>(
    []
  ); // initialize
  const [userSettingsData, setUserSettingsData] = useState<
    SettingsDataObject[]
  >([]); // initialize
  const [userRoundsData, setUserRoundsData] = useState<RoundsDataObject[]>([]); // initialize
  const [gotData, setGotData] = useState(false);

  /**
   * A function to retrieve all data tied to a user from the Firestore database and set the
   * userAccountData, userSettingsData, and userRoundsData useState variables with the retrieved values.
   */
  const dbConnectAndGetUserData = async () => {
    //0 security atm. For production, adjust security settings:
    //https://stackoverflow.com/questions/46590155/firestore-permission-denied-missing-or-insufficient-permissions
    const db = userContext.db;
    const user = userContext.user
    if (!db || !user) return;

    const accountData: AccountDataObject[] = [];

    const userAccount = await getDoc(doc(db, "users", user.uid));

    if(userAccount.exists()) {
      
      let userAccountData : DocumentData = userAccount.data();
      let buildUserData : AccountDataObject = {
        uID: user.uid,
        loginUsername: "",
        emailAddress: "",
      }

      if (userAccountData.loginUsername) {
        buildUserData.loginUsername = userAccountData.loginUsername;
      }

      if (userAccountData.emailAddress) {
        buildUserData.emailAddress = userAccountData.emailAddress;
      }

      accountData.push(buildUserData)

    }

    setUserAccountData(accountData);

    //********************************************************** */

    const settingsData: SettingsDataObject[] = [];

    const settingPrefs = await getDoc(doc(db, "settings", user.uid))

    if (settingPrefs.exists()) {
      let data : DocumentData = settingPrefs.data();
      let build : SettingsDataObject = {
        userUID: user.uid,
        backgroundColor: "",
        targetColors: [],
        projectileColor: "",
        targetVel: NaN,
        targetAcc: NaN,
        duration: NaN,
      }

      if (data.backgroundColor) {
        build.backgroundColor = data.backgroundColor;
      }
      if (data.projectileColor) {
        build.projectileColor = data.projectileColor;
      }
      if (data.targetColors) {
        build.targetColors = data.targetColors;
      }
      if (data.targetVel) {
        build.targetVel = data.targetVel;
      }
      if (data.targetAcc) {
        build.targetAcc = data.targetAcc;
      }
      if (data.duration) {
        build.duration = data.duration;
      }
      settingsData.push(build)
    }

    setUserSettingsData(settingsData);

    //********************************************************** */

    const roundsData: RoundsDataObject[] = [];

    const roundsRef: CollectionReference<DocumentData> = collection(
      db,
      "rounds"
    );

    const qRounds: Query<DocumentData> = query(
      roundsRef,
      where("userID", "==", userContext.user?.uid)
    );

    const querySnapshotRounds: QuerySnapshot<DocumentData> = await getDocs(
      qRounds
    );

    querySnapshotRounds.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      roundsData.push({
        userID: doc.data().userID,
        timestamp: doc.data().timestamp,
        type: doc.data().type,
        precision: doc.data().precision,
        accuracy: doc.data().accuracy,
        score: doc.data().score,
        difficulty: doc.data().difficulty,
      });
    });

    setUserRoundsData(roundsData);

    setGotData(true);
  };

  useEffect(() => {
    if (!gotData) dbConnectAndGetUserData();
  }, [gotData, userContext.db]);

  return (
    <div className="h-screen flex flex-col">
      <Navbar />
      <div className="w-screen flex-1">
        {gotData ? (
          <GDPR
            userAccountData={userAccountData}
            userSettingsData={userSettingsData}
            userRoundsData={userRoundsData}
          />
        ) : (
          <GDPR
            userAccountData={null}
            userSettingsData={null}
            userRoundsData={null}
          />
        )}
      </div>
    </div>
  );
};

export default UserData;
